import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '../../../components/inputs/textInput';
import { CustomButton } from '../../../components/buttons/customButton';
import { sendGAcode, uninstallGA } from '../../../apiHooks/mutations/sendGA4/sendGAcode';
import useGetGACodes from '../../../apiHooks/queries/GAcodes/useGetGACodes';
import LoadingSpinner from '../../../components/loadingSpinner/loadingSpinner';

const AnalyticsPanel = () => {
    const { t } = useTranslation();

    const [codes, setCodes] = useState({
        gaMeasurementId: '',
        ga4Secret: '',
    });

    const {
        data: fetchedData,
        refetch,
        isLoading: isFetching,
    } = useGetGACodes('analytics_panel');

    const { mutate: saveGA, isLoading: isSaving } = sendGAcode();
    const { mutate: uninstall, isLoading: isUninstalling } = uninstallGA();

    const isActionLoading = isFetching || isSaving || isUninstalling;

    const fetchedGaId = fetchedData?.data?.data?.gaMeasurementId;
    const fetchedSecret = fetchedData?.data?.data?.ga4Secret;
    const isLinked = Boolean(fetchedGaId || fetchedSecret);

    const formValid = codes.gaMeasurementId.trim() !== '' && codes.ga4Secret.trim() !== '';

    useEffect(() => {
        if (isLinked) {
            setCodes({
                gaMeasurementId: fetchedGaId || '',
                ga4Secret: fetchedSecret || '',
            });
        } else {
            setCodes({
                gaMeasurementId: '',
                ga4Secret: '',
            });
        }
    }, [fetchedGaId, fetchedSecret, isLinked]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCodes(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = () => {
        if (!formValid) return;
        saveGA(
            {
                gaMeasurementId: codes.gaMeasurementId.trim(),
                ga4Secret: codes.ga4Secret.trim(),
            },
            {
                onSuccess: () => refetch(),
            }
        );
    };

    const handleUninstall = () => {
        uninstall(undefined, {
            onSuccess: () => refetch(),
        });
    };

    if (isFetching) return <LoadingSpinner variant="primary" size={50} />;

    return (
        <div className="flex flex-col gap-5 rounded-xl border p-4 md:flex-row md:justify-between">
            <div className="flex flex-col gap-2 text-primary">
                <span className="text-sm font-semibold">
                    {isLinked ? 'Connected' : 'Connect to your Google Id Account'}
                </span>
                {!isLinked ? (
                    <p className="text-sm font-normal">
                        Here you can link your Google Account <br />with Digger for more efficient tracking
                    </p>
                ) : (
                    <p className="text-sm font-normal">
                        Your data will now be reported to Google Analytics 4!
                    </p>
                )}
            </div>

            <div className="flex flex-col gap-2 md:flex-row md:items-center text-primary">
                <TextInput
                    name="gaMeasurementId"
                    value={codes.gaMeasurementId}
                    label="GA Measurement Id"
                    size="small"
                    placeholder="123-123-1234"
                    onChange={handleChange}
                />
                <TextInput
                    name="ga4Secret"
                    value={codes.ga4Secret}
                    label="GA4 Secret"
                    size="small"
                    placeholder="Enter GA4 Secret"
                    onChange={handleChange}
                />
                <CustomButton
                    loading={isActionLoading}
                    disabled={!isLinked && !formValid}
                    type="button"
                    onClick={isLinked ? handleUninstall : handleSubmit}
                >
                    {isLinked ? t('uninstallGA') : t('linkAccount')}
                </CustomButton>
            </div>
        </div>
    );
};

export default AnalyticsPanel;
